import React, { useState } from 'react';
import HeaderSection from '../content/HeaderSection';
import FooterSection from '../content/FooterSection';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';
import { usePageLanguage } from '../context/PageLanguageContext';
import SEOHead from '../components/SEOHead';
import DesktopManual from './manual/DesktopManual';
import WebManual from './manual/WebManual';

// Translation object
const translations = {
  en: {
    pageTitle: 'Scoreboard Manual - Rink Hockey Scoreboard',
    pageDescription: 'Complete manual for the Rink Hockey Scoreboard application. Learn how to use all features including period management, penalties, timeouts, and entertainment system.',
    scoreboardTab: 'Scoreboard',
    entertainmentTab: 'Entertainment',
    desktopTab: 'Desktop Edition',
    scoreboardManual: 'Scoreboard Manual',
    entertainmentManual: 'Entertainment Manual',
    desktopManual: 'Desktop Edition — step by step'
  },
  de: {
    pageTitle: 'Scoreboard Anleitung - Rink Hockey Scoreboard',
    pageDescription: 'Vollständige Anleitung für die Rink Hockey Scoreboard Anwendung. Erfahren Sie, wie Sie alle Funktionen nutzen, einschließlich Periodenverwaltung, Strafen, Timeouts und Entertainment-System.',
    scoreboardTab: 'Scoreboard',
    entertainmentTab: 'Entertainment',
    desktopTab: 'Desktop Edition',
    scoreboardManual: 'Scoreboard Anleitung',
    entertainmentManual: 'Entertainment Anleitung',
    desktopManual: 'Desktop Edition — Schritt für Schritt'
  }
};

const Manual = () => {
  useGoogleAnalytics();
  const { pageLanguage } = usePageLanguage();
  const t = translations[pageLanguage] || translations.en;
  const L = (en, de) => (pageLanguage === 'de' ? de : en);
  const [activeTab, setActiveTab] = useState('scoreboard');
  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead 
        title={activeTab === 'scoreboard' ? t.pageTitle : t.pageTitle.replace('Scoreboard', 'Entertainment')}
        description={t.pageDescription}
        lang={pageLanguage}
        canonical={`https://scoreboard.tomroethlisberger.ch/manual`}
      />
      <HeaderSection />
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="mb-8">
            <nav className="flex space-x-4 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('scoreboard')}
                className={`py-4 px-6 font-medium text-sm focus:outline-none ${
                  activeTab === 'scoreboard'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.scoreboardTab}
              </button>
              <button
                onClick={() => setActiveTab('entertainment')}
                className={`py-4 px-6 font-medium text-sm focus:outline-none ${
                  activeTab === 'entertainment'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.entertainmentTab}
              </button>
              <button
                onClick={() => setActiveTab('desktop')}
                className={`py-4 px-6 font-medium text-sm focus:outline-none ${
                  activeTab === 'desktop'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.desktopTab}
              </button>
            </nav>
          </div>
          <h1 className="text-4xl font-bold mb-8">
            {activeTab === 'scoreboard' ? t.scoreboardManual : activeTab === 'desktop' ? t.desktopManual : t.entertainmentManual}
          </h1>
          
          {activeTab === 'desktop' ? (
            <DesktopManual lang={pageLanguage === 'de' ? 'de' : 'en'} />
          ) : activeTab === 'scoreboard' ? (
            <>
            <WebManual variant="free" lang={pageLanguage === 'de' ? 'de' : 'en'} />

            <div className="my-14 border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-semibold text-gray-900">{L('Reference: all details', 'Nachschlagen: alle Details')}</h2>
              <p className="mt-2 text-gray-600">
                {L(
                  'The step-by-step guide above covers everyday use. Below is the complete reference, including the rules the board applies by itself.',
                  'Die Anleitung oben deckt den Alltag ab. Darunter folgt die vollständige Referenz — inklusive der Regeln, die die Anzeigetafel selbst anwendet.'
                )}
              </p>
            </div>
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{L('Overview', 'Überblick')}</h2>
            <p className="mb-4">
              {L(
                'The rink hockey scoreboard is a comprehensive digital scoring system designed for rink hockey matches. It features a main control panel and a separate live view screen that can be displayed on a secondary monitor.',
                'Das Rink Hockey Scoreboard ist ein umfassendes digitales Anzeigesystem für Rollhockey-Spiele. Es besteht aus einem Kontroll-Panel (Bedienoberfläche) und einer separaten LiveView-Anzeige, die auf einem zweiten Bildschirm angezeigt werden kann.'
              )}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Period Management', 'Periodenverwaltung')}</h2>
            <div className="space-y-4">
              <h3 className="text-xl font-medium">{L('Regular Periods', 'Reguläre Perioden')}</h3>
              <p>
                {L(
                  'A rink hockey match consists of two regular periods by default. Each period runs for the configured game duration (set in settings). The scoreboard automatically manages period transitions and break periods.',
                  'Ein Rollhockey-Spiel besteht standardmäßig aus zwei regulären Perioden. Jede Periode läuft mit der in den Einstellungen konfigurierten Spielzeit. Das Scoreboard verwaltet Periodenwechsel und Pausen automatisch.'
                )}
              </p>
              
              <h3 className="text-xl font-medium">{L('Period Features', 'Funktionen')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('Automatic period counting (1-4)', 'Automatische Periodenzählung (1–4)')}</li>
                <li>{L('Configurable period duration for regular periods', 'Konfigurierbare Spielzeit für reguläre Perioden')}</li>
                <li>{L('Visual period indicator', 'Visueller Periodenindikator')}</li>
                <li>{L('Automatic break period initiation when a period ends', 'Automatischer Pausenstart beim Ende einer Periode')}</li>
                <li>{L('Period controls (increment/decrement) for manual adjustments', 'Manuelle Periodensteuerung (hoch/runter) für Korrekturen')}</li>
              </ul>

              <h3 className="text-xl font-medium">{L('Overtime Periods', 'Verlängerung')}</h3>
              <p>
                {L(
                  'If the match requires overtime, the scoreboard supports up to two overtime periods (periods 3 and 4). Overtime periods have their own configurable duration, which can be different from regular period duration.',
                  'Wenn das Spiel in die Verlängerung geht, unterstützt das Scoreboard bis zu zwei Verlängerungsperioden (Periode 3 und 4). Für die Verlängerung kann eine eigene Spielzeit konfiguriert werden, die von der regulären Spielzeit abweichen darf.'
                )}
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                <h4 className="font-medium mb-2 text-blue-700">{L('Automatic Overtime Logic:', 'Automatische Verlängerungslogik:')}</h4>
                <ul className="list-disc pl-6 space-y-1 text-blue-700">
                  <li>{L('After period 1 ends: Always advances to period 2', 'Nach Periode 1: Wechsel immer zu Periode 2')}</li>
                  <li>{L('After period 2 ends: Only advances to period 3 (overtime) if the score is tied', 'Nach Periode 2: Wechsel zu Periode 3 (Verlängerung) nur bei Unentschieden')}</li>
                  <li>{L('After period 3 ends: Only advances to period 4 (second overtime) if the score is still tied', 'Nach Periode 3: Wechsel zu Periode 4 nur wenn weiterhin Unentschieden')}</li>
                  <li>{L('If a team is leading after period 2 or 3, the game ends without advancing to overtime', 'Wenn nach Periode 2 oder 3 eine Mannschaft führt, endet das Spiel ohne weitere Verlängerung')}</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Team Fouls Management', 'Teamfouls')}</h2>
            <div className="space-y-4">
              <p>
                {L(
                  'The scoreboard tracks team fouls for both teams, with special handling for the 10th and subsequent fouls.',
                  'Das Scoreboard zählt die Teamfouls für beide Mannschaften und behandelt den 10. sowie alle weiteren Fouls mit einer besonderen Hervorhebung.'
                )}
              </p>
              
              <h3 className="text-xl font-medium">{L('Foul Counter Features', 'Funktionen des Foul-Zählers')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('Individual foul counters for each team', 'Separate Foul-Zähler für jedes Team')}</li>
                <li>{L('Visual indicators for foul counts:', 'Visuelle Hinweise:')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('Normal display for fouls 1-9', 'Normale Anzeige für Fouls 1–9')}</li>
                    <li>{L('Red highlight for 10th foul and beyond', 'Rote Markierung ab dem 10. Foul')}</li>
                    <li>{L('Large, clear numbers for easy visibility', 'Große, gut lesbare Zahlen')}</li>
                  </ul>
                </li>
                <li>{L('Controls for foul management:', 'Bedienelemente:')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('Increment button to add fouls', 'Plus-Taste zum Hinzufügen')}</li>
                    <li>{L('Decrement button to correct mistakes', 'Minus-Taste zum Korrigieren')}</li>
                    <li>{L('Quick reset option available', 'Schnell-Reset verfügbar')}</li>
                  </ul>
                </li>
              </ul>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">{L('Important Rules:', 'Wichtige Hinweise:')}</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{L('10th team foul triggers special visual indicator', 'Ab dem 10. Teamfoul wird eine spezielle Markierung angezeigt')}</li>
                  <li>{L('Foul count persists through all periods', 'Teamfouls bleiben über alle Perioden bestehen')}</li>
                  <li>{L('Foul counters can be adjusted at any time if needed', 'Korrekturen sind jederzeit möglich')}</li>
                  <li>{L('Both live view and control panel show current foul counts', 'LiveView und Control Panel zeigen stets den aktuellen Stand')}</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Personal Fouls and Penalties', 'Persönliche Strafen')}</h2>
            <div className="space-y-4">
              <p>
                {L(
                  'The scoreboard includes a comprehensive system for tracking individual player penalties and their durations.',
                  'Das Scoreboard enthält ein umfangreiches System zur Erfassung persönlicher Strafen inklusive Laufzeiten.'
                )}
              </p>
              
              <h3 className="text-xl font-medium">{L('Penalty Management Features', 'Funktionen der Strafverwaltung')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('Individual penalty tracking per player:', 'Individuelle Strafen pro Spieler:')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('Player number identification', 'Spielernummer')}</li>
                    <li>{L('Multiple concurrent penalties support', 'Mehrere gleichzeitige Strafen')}</li>
                    <li>{L('Automatic penalty time tracking', 'Automatischer Countdown')}</li>
                  </ul>
                </li>
                <li>{L('Penalty durations:', 'Strafdauern:')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('2-minute minor penalties', '2-Minuten-Strafen')}</li>
                    <li>{L('5-minute major penalties', '5-Minuten-Strafen')}</li>
                    <li>{L('10-minute misconduct penalties', '10-Minuten-Strafen')}</li>
                    <li>{L('Custom duration penalties for special cases', 'Sonderfälle mit individueller Dauer')}</li>
                  </ul>
                </li>
                <li>{L('Visual indicators:', 'Anzeige:')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('Countdown timer for each active penalty', 'Countdown für jede aktive Strafe')}</li>
                    <li>{L('Clear display of player number and remaining time', 'Klare Anzeige von Spielernummer und Restzeit')}</li>
                    <li>{L('Automatic removal when penalty expires', 'Automatisches Entfernen nach Ablauf')}</li>
                  </ul>
                </li>
              </ul>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">{L('Penalty Management Tips:', 'Tipps zur Strafverwaltung:')}</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{L('Penalties can be added at any time during the game', 'Strafen können jederzeit während des Spiels hinzugefügt werden')}</li>
                  <li>{L('Multiple penalties for the same player stack in sequence', 'Mehrere Strafen für denselben Spieler laufen nacheinander')}</li>
                  <li>{L('Penalty timers pause when game timer is stopped', 'Strafzeiten pausieren, wenn die Spielzeit pausiert ist')}</li>
                  <li>{L('Manual removal option available if needed', 'Bei Bedarf können Strafen manuell entfernt werden')}</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Team Inferiority Display', 'Unterzahl-Anzeige')}</h2>
            <div className="space-y-4">
              <p>
                {L(
                  'The Team Inferiority feature provides real-time tracking of cumulative penalty time for each team, helping officials and timekeepers monitor when teams are playing short-handed.',
                  'Die Unterzahl-Anzeige berechnet die kumulierte Strafzeit pro Team in Echtzeit und hilft dabei, Unterzahl-Situationen übersichtlich zu verfolgen.'
                )}
              </p>
              
              <h3 className="text-xl font-medium">{L('How It Works', 'So funktioniert es')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('Automatically calculates cumulative penalty time when a team has 2 or more active penalties', 'Automatische Berechnung der kumulierten Strafzeit ab 2 aktiven Strafen')}</li>
                <li>{L('Displays countdown timers for both home and away teams', 'Anzeige der Countdown-Zeiten für Heim- und Gastteam')}</li>
                <li>{L('Shows below the PENALTIES section in both control panel and live view', 'Erscheint unterhalb von STRAFEN sowohl im Control Panel als auch im LiveView')}</li>
                <li>{L('Updates in real-time as penalties expire or new ones are added', 'Aktualisiert sich in Echtzeit bei Ablauf oder Hinzufügen von Strafen')}</li>
              </ul>

              <h3 className="text-xl font-medium">{L('Calculation Logic', 'Berechnungslogik')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('1 penalty: Shows individual penalty time (no inferiority time)', '1 Strafe: Anzeige der individuellen Strafzeit (keine Unterzahlzeit)')}</li>
                <li>{L('2+ penalties: Shows cumulative time of all active penalties', '2+ Strafen: Anzeige der kumulierten Zeit aller aktiven Strafen')}</li>
                <li>{L('Penalties are served sequentially (one after another)', 'Strafen werden sequenziell abgesessen (nacheinander)')}</li>
                <li>{L('Timer automatically adjusts as penalties expire', 'Countdown passt sich automatisch an, wenn Strafen ablaufen')}</li>
              </ul>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                <h4 className="font-medium mb-2 text-blue-700">{L('Beta Feature:', 'Beta-Funktion:')}</h4>
                <p className="text-blue-700">
                  {L(
                    'This feature can be enabled/disabled in Settings → Beta Features → Team Inferiority Display. It is disabled by default and must be manually activated.',
                    'Diese Funktion kann unter Einstellungen → Beta Features → Unterzahl-Anzeige aktiviert/deaktiviert werden. Sie ist standardmäßig deaktiviert und muss manuell eingeschaltet werden.'
                  )}
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Language Control', 'Sprachsteuerung')}</h2>
            <div className="space-y-4">
              <p>
                {L(
                  'The scoreboard features a dual language system with separate language settings for the control panel and the live view display, allowing operators to work in their preferred language while displaying information to viewers in a different language.',
                  'Das Scoreboard verfügt über ein Dual-Language-System mit separaten Spracheinstellungen für das Control Panel und die LiveView-Anzeige. So kannst du die Bedienung in deiner bevorzugten Sprache nutzen und gleichzeitig den Zuschauern eine andere Sprache anzeigen.'
                )}
              </p>
              
              <h3 className="text-xl font-medium">{L('Two Independent Language Settings', 'Zwei unabhängige Spracheinstellungen')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>{L('Control Panel Language:', 'Bediensprache (Control Panel):')}</strong> {L('Affects all menus, settings, and buttons in the operator interface', 'Betrifft alle Menüs, Einstellungen und Buttons in der Bedienoberfläche')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('Settings menus and labels', 'Einstellungs-Menüs und Beschriftungen')}</li>
                    <li>{L('Button texts and controls', 'Button-Texte und Bedienelemente')}</li>
                    <li>{L('Modal dialogs and confirmations', 'Dialoge und Bestätigungen')}</li>
                    <li>{L('Entertainment settings', 'Entertainment-Einstellungen')}</li>
                  </ul>
                </li>
                <li><strong>{L('Dashboard Language (Scoreboard/LiveView):', 'Anzeigesprache (Scoreboard/LiveView):')}</strong> {L('Affects what viewers see on the live display', 'Betrifft das, was Zuschauer auf der Anzeige sehen')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('Team names (HOME TEAM / AWAY TEAM defaults)', 'Teamnamen (Standard: HEIMTEAM/GASTTEAM)')}</li>
                    <li>{L('Labels: PERIOD, TEAMFOULS, PENALTIES, TEAM INFERIORITY', 'Labels: PERIODE, TEAMFOULS, STRAFEN, UNTERZAHL')}</li>
                    <li>{L('Timeout and break overlays', 'Timeout- und Pausen-Overlays')}</li>
                  </ul>
                </li>
              </ul>

              <h3 className="text-xl font-medium">{L('Supported Languages', 'Unterstützte Sprachen')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('English (EN) - Default language', 'Englisch (EN) – Standardsprache')}</li>
                <li>{L('German (DE) - Deutsch', 'Deutsch (DE)')}</li>
              </ul>

              <h3 className="text-xl font-medium">{L('How to Change Languages', 'Sprache ändern')}</h3>
              <ol className="list-decimal pl-6 space-y-2">
                <li>{L('Open Settings from the main menu', 'Öffne die Einstellungen im Hauptmenü')}</li>
                <li>{L('Navigate to the "General Settings" tab', 'Wechsle zum Tab „Allgemeine Einstellungen“')}</li>
                <li>{L('Find the "Language" section at the top', 'Suche oben den Abschnitt „Sprache“')}</li>
                <li>{L('Select your preferred language for each setting:', 'Wähle für beide Bereiche die gewünschte Sprache:')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('Control Panel Language (for your interface)', 'Bediensprache (für deine Bedienoberfläche)')}</li>
                    <li>{L('Dashboard Language (for viewers)', 'Anzeigesprache (für Zuschauer)')}</li>
                  </ul>
                </li>
                <li>{L('Changes apply immediately', 'Änderungen werden sofort übernommen')}</li>
              </ol>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">{L('Language Persistence:', 'Speicherung:')}</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{L('Both language settings are saved in browser local storage', 'Beide Spracheinstellungen werden im Browser gespeichert')}</li>
                  <li>{L('Settings persist between sessions', 'Einstellungen bleiben zwischen Sitzungen erhalten')}</li>
                  <li>{L('Each setting can be changed independently', 'Beide Einstellungen sind unabhängig voneinander')}</li>
                  <li>{L('Default language is English for both settings', 'Standardsprache ist für beide Einstellungen Englisch')}</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Buzzer System', 'Buzzer-System')}</h2>
            <div className="space-y-4">
              <p>
                {L(
                  'The scoreboard includes an automatic buzzer system that sounds in the following situations:',
                  'Das Scoreboard enthält ein automatisches Buzzer-System, das in folgenden Situationen ertönt:'
                )}
              </p>
              
              <h3 className="text-xl font-medium">{L('When the Buzzer Sounds', 'Wann der Buzzer ertönt')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('When the game timer reaches 00:00', 'Wenn die Spielzeit 00:00 erreicht')}</li>
                <li>{L('When a timeout is requested and the game timer is paused', 'Wenn ein Timeout angefordert wird und die Spielzeit pausiert ist')}</li>
                <li>{L('When the timeout reaches 15 seconds before end', 'Wenn beim Timeout noch 15 Sekunden verbleiben')}</li>
                <li>{L('When the break timer reaches 00:00', 'Wenn der Pausen-Timer 00:00 erreicht')}</li>
              </ul>

              <p>
                {L(
                  'The buzzer helps officials and players stay synchronized with game events and ensures clear communication of important moments during the match.',
                  'Der Buzzer hilft Offiziellen und Spielern, bei Spielereignissen synchron zu bleiben, und sorgt für eine klare Kommunikation wichtiger Momente während des Spiels.'
                )}
              </p>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">{L('Period Timing Logic:', 'Perioden-Zeitlogik:')}</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{L('Periods 1-2: Use regular game duration (e.g., 25:00)', 'Perioden 1–2: Reguläre Spielzeit (z.B. 25:00)')}</li>
                  <li>{L('Periods 3-4: Use overtime duration (e.g., 10:00)', 'Perioden 3–4: Verlängerungszeit (z.B. 10:00)')}</li>
                  <li>{L('Each period starts with its full configured duration', 'Jede Periode startet mit der konfigurierten vollen Zeit')}</li>
                  <li>{L('Timer counts down to 00:00', 'Timer zählt herunter bis 00:00')}</li>
                  <li>{L('Break period automatically starts when timer reaches 00:00', 'Beim Erreichen von 00:00 startet automatisch die Pause')}</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Break Periods', 'Pausen')}</h2>
            <div className="space-y-4">
              <p>
                {L('Break periods occur automatically between game periods. During breaks:', 'Pausen werden automatisch zwischen den Perioden gestartet. Während der Pause:')}
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('A break overlay appears on the live screen', 'Ein Pausen-Overlay erscheint im LiveView')}</li>
                <li>{L('Break timer counts down the configured break duration', 'Ein Timer zählt die konfigurierte Pausenzeit herunter')}</li>
                <li>{L('Optional image carousel displays on the live screen', 'Optional wird ein Bild-Karussell angezeigt')}</li>
                <li>{L('Clear indication of the upcoming period', 'Klare Anzeige der nächsten Periode')}</li>
                <li>{L('Break can be skipped if needed', 'Die Pause kann bei Bedarf beendet/übersprungen werden')}</li>
              </ul>
              
              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">{L('Automatic Break Flow:', 'Automatischer Ablauf:')}</h4>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>{L('When a period timer reaches 00:00, the buzzer sounds', 'Wenn der Perioden-Timer 00:00 erreicht, ertönt der Buzzer')}</li>
                  <li>{L('The system automatically advances to the next period', 'Das System wechselt automatisch in die nächste Periode')}</li>
                  <li>{L('The break overlay starts with the appropriate duration:', 'Das Pausen-Overlay startet mit der passenden Dauer:')}
                    <ul className="list-disc pl-6 mt-1">
                      <li>{L('Period 1 → 2: Standard break duration (default: 10 minutes)', 'Periode 1 → 2: Standardpause (Default: 10 Minuten)')}</li>
                      <li>{L('Period 2 → 3: Overtime break 1 duration (default: 3 minutes)', 'Periode 2 → 3: Verlängerungspause 1 (Default: 3 Minuten)')}</li>
                      <li>{L('Period 3 → 4: Overtime break 2 duration (default: 2 minutes)', 'Periode 3 → 4: Verlängerungspause 2 (Default: 2 Minuten)')}</li>
                    </ul>
                  </li>
                  <li>{L('When the break timer reaches 00:00, the buzzer sounds again', 'Wenn der Pausen-Timer 00:00 erreicht, ertönt der Buzzer erneut')}</li>
                  <li>{L('The break overlay closes and the next period is ready to start', 'Das Overlay schließt und die nächste Periode kann gestartet werden')}</li>
                </ol>
                <p className="mt-2 text-sm text-gray-600">
                  <strong>{L('Note:', 'Hinweis:')}</strong>{' '}
                  {L(
                    'All break durations can be configured in the settings. Breaks only occur when advancing to the next period (based on overtime logic).',
                    'Alle Pausendauern können in den Einstellungen konfiguriert werden. Pausen treten nur beim Wechsel in die nächste Periode auf (abhängig von der Verlängerungslogik).'
                  )}
                </p>
              </div>
            </div>
          </section>
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Timeout Management', 'Timeouts')}</h2>
            <div className="space-y-4">
              <h3 className="text-xl font-medium">{L('Timeout System', 'Timeout-System')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('Each team has one timeout per match', 'Jedes Team hat ein Timeout pro Spiel')}</li>
                <li>{L('Two-step timeout process:', 'Zweistufiger Ablauf:')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('Step 1: Request timeout (yellow indicator)', 'Schritt 1: Timeout anfordern (gelber Hinweis)')}</li>
                    <li>{L('Step 2: Start timeout when ready (activates the timer)', 'Schritt 2: Timeout starten (Timer läuft)')}</li>
                  </ul>
                </li>
                <li>{L('60-second countdown timer with clear visual overlay', '60-Sekunden-Countdown mit Overlay')}</li>
                <li>{L('Visual warning at 15 seconds (timer turns red)', 'Warnung bei 15 Sekunden (Timer wird rot)')}</li>
                <li>{L('Audio buzzer sounds at 15 seconds remaining', 'Buzzer ertönt bei 15 Sekunden Restzeit')}</li>
                <li>{L('Timeout requests can be cancelled before activation', 'Anforderungen können vor Start abgebrochen werden')}</li>
                <li>{L('Active timeouts show on both control panel and live view', 'Aktive Timeouts werden im Control Panel und LiveView angezeigt')}</li>
                <li>{L('Clear visual and audio signals when timeout ends', 'Klare visuelle und akustische Signale beim Ende')}</li>
              </ul>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">{L('Timeout Process:', 'Ablauf:')}</h4>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>{L('Click the timeout button for the desired team (requests timeout)', 'Timeout-Button beim gewünschten Team klicken (Timeout anfordern)')}</li>
                  <li>{L('Yellow indicator shows pending timeout request', 'Gelber Hinweis zeigt die ausstehende Anforderung')}</li>
                  <li>{L('Click "Start Timeout" to begin the 60-second countdown', '„Timeout starten“ klicken, um den Countdown zu starten')}</li>
                  <li>{L('Timeout overlay appears with countdown timer', 'Timeout-Overlay erscheint mit Countdown')}</li>
                  <li>{L('System automatically ends timeout after 60 seconds', 'Das System beendet das Timeout automatisch nach 60 Sekunden')}</li>
                </ol>
              </div>
            </div>
          </section>
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Live Screen Functionality', 'LiveView-Anzeige')}</h2>
            <div className="space-y-4">
              <h3 className="text-xl font-medium">{L('Dual Screen Setup', 'Zwei-Bildschirm-Setup')}</h3>
              <p>
                {L(
                  'The scoreboard supports a dual-screen setup where the control panel remains on your primary screen while the live view is automatically positioned on your secondary display. The live view can be toggled to fullscreen mode for optimal visibility.',
                  'Das Scoreboard unterstützt ein Zwei-Bildschirm-Setup: Das Control Panel bleibt auf dem Hauptbildschirm, während die LiveView automatisch auf dem zweiten Bildschirm positioniert wird. Die LiveView kann für optimale Sichtbarkeit in den Vollbildmodus wechseln.'
                )}
              </p>
              
              <h3 className="text-xl font-medium">{L('Display Elements', 'Anzeige-Elemente')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('Team names and scores', 'Teamnamen und Spielstand')}</li>
                <li>{L('Game timer with period indication', 'Spielzeit inkl. Periodenanzeige')}</li>
                <li>{L('Team fouls counter', 'Teamfoul-Zähler')}</li>
                <li>{L('Active penalties with countdown timers', 'Aktive Strafen mit Countdowns')}</li>
                <li>{L('Timeout overlays', 'Timeout-Overlays')}</li>
                <li>{L('Break period overlays', 'Pausen-Overlays')}</li>
                <li>{L('Custom image carousel during breaks', 'Bild-Karussell während Pausen')}</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Configurable Settings', 'Einstellungen')}</h2>
            <div className="space-y-4">
              <p>
                {L('The following settings can be configured through the settings panel:', 'Folgende Einstellungen können angepasst werden:')}
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>{L('Game Duration:', 'Spielzeit:')}</strong> {L('Set the default period length for regular periods (format: MM:SS)', 'Standarddauer für reguläre Perioden (Format: MM:SS)')}</li>
                <li><strong>{L('Overtime Duration:', 'Verlängerung:')}</strong> {L('Configure the length of overtime periods (format: MM:SS)', 'Dauer der Verlängerungsperioden (Format: MM:SS)')}</li>
                <li><strong>{L('Break Duration (P1→P2):', 'Pause (P1→P2):')}</strong> {L('Set the length of break between period 1 and 2 (format: MM:SS)', 'Pausendauer zwischen Periode 1 und 2 (Format: MM:SS)')}</li>
                <li><strong>{L('Break Duration (P2→P3):', 'Pause (P2→P3):')}</strong> {L('Set the length of break between period 2 and 3 (format: MM:SS)', 'Pausendauer zwischen Periode 2 und 3 (Format: MM:SS)')}</li>
                <li><strong>{L('Break Duration (P3→P4):', 'Pause (P3→P4):')}</strong> {L('Set the length of break between period 3 and 4 (format: MM:SS)', 'Pausendauer zwischen Periode 3 und 4 (Format: MM:SS)')}</li>
                <li><strong>{L('Background Color:', 'Hintergrundfarbe:')}</strong> {L('Customize the scoreboard background color', 'Farbe des Scoreboard-Hintergrunds')}</li>
                <li><strong>{L('Carousel Images:', 'Karussell-Bilder:')}</strong> {L('Upload up to 6 images to display during breaks on the scoreboard', 'Bis zu 6 Bilder für die Anzeige während Pausen auf dem Scoreboard')}</li>
                <li><strong>{L('Break Overlay Images:', 'Pausen-Overlay Bilder:')}</strong> {L('Upload up to 4 images to display on the break overlay', 'Bis zu 4 Bilder für das Pausen-Overlay')}</li>
                <li><strong>{L('Buzzer Sound:', 'Buzzer:')}</strong> {L('Enable/disable the automatic buzzer system', 'Automatischen Buzzer ein-/ausschalten')}</li>
                <li><strong>{L('Buzzer Volume:', 'Buzzer-Lautstärke:')}</strong> {L('Adjust the volume of the buzzer sound', 'Lautstärke des Buzzers einstellen')}</li>
              </ul>

              <h3 className="text-xl font-medium mt-4">{L('Beta Features', 'Beta-Funktionen')}</h3>
              <p>
                {L('Experimental features that can be enabled in Settings → Beta Features:', 'Experimentelle Funktionen, die unter Einstellungen → Beta Features aktiviert werden können:')}
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>{L('Auto Break:', 'Auto Break:')}</strong> {L('Automatically start break timer when a period ends (enabled by default)', 'Startet automatisch die Pause, wenn eine Periode endet (standardmäßig aktiviert)')}</li>
                <li><strong>{L('Team Inferiority Display:', 'Unterzahl-Anzeige:')}</strong> {L('Show cumulative penalty time for teams in control view (disabled by default)', 'Zeigt kumulierte Strafzeit pro Team an (standardmäßig deaktiviert)')}</li>
              </ul>
              
              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">{L('Image Guidelines:', 'Bild-Hinweise:')}</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{L('Maximum of 5 images', 'Maximal 5 Bilder')}</li>
                  <li>{L('Images are automatically compressed and resized', 'Bilder werden automatisch komprimiert und skaliert')}</li>
                  <li>{L('Maximum dimensions: 800x600 pixels', 'Maximale Abmessungen: 800×600 Pixel')}</li>
                  <li>{L('Supported format: JPEG', 'Unterstütztes Format: JPEG')}</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Data Storage', 'Datenspeicherung')}</h2>
            <div className="space-y-4">
              <p>
                {L(
                  "The scoreboard uses your browser's local storage to save and persist the game state. This means:",
                  'Das Scoreboard nutzt den lokalen Speicher deines Browsers, um den Spielstand und Einstellungen zu sichern. Das bedeutet:'
                )}
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('All game data is stored locally in your browser', 'Alle Spieldaten werden lokal im Browser gespeichert')}</li>
                <li>{L('Data persists even if you close and reopen the browser', 'Daten bleiben auch nach Schließen/Neustart erhalten')}</li>
                <li>{L('No internet connection required after initial page load', 'Nach dem Laden ist keine Internetverbindung nötig')}</li>
                <li>{L('Data is private and stays on your device', 'Daten sind privat und bleiben auf deinem Gerät')}</li>
              </ul>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">{L('Stored Information:', 'Gespeicherte Informationen:')}</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{L('Team names and scores', 'Teamnamen und Spielstand')}</li>
                  <li>{L('Current period and time', 'Aktuelle Periode und Zeit')}</li>
                  <li>{L('Active penalties', 'Aktive Strafen')}</li>
                  <li>{L('Game settings (period durations, break duration)', 'Spieleinstellungen (Perioden- und Pausendauern)')}</li>
                  <li>{L('Custom background color', 'Individuelle Hintergrundfarbe')}</li>
                  <li>{L('Carousel images (as compressed base64 data)', 'Karussell-Bilder (komprimiert als Base64)')}</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
                <p className="text-yellow-700">
                  <strong>{L('Note:', 'Hinweis:')}</strong>{' '}
                  {L(
                    "Clearing your browser's data or using private/incognito mode will reset all scoreboard settings and game state.",
                    'Wenn du Browserdaten löschst oder den privaten Modus nutzt, werden Einstellungen und Spielstand zurückgesetzt.'
                  )}
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{L('Keyboard Shortcuts', 'Tastenkürzel')}</h2>
            <div className="space-y-4">
              <p>
                {L('The scoreboard supports various keyboard shortcuts for quick control:', 'Das Scoreboard unterstützt Tastenkürzel für schnelle Bedienung:')}
              </p>
              
              <h3 className="text-xl font-medium">{L('Game Control', 'Spielsteuerung')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>{L('S Key:', 'Taste S:')}</strong> {L('Start/Stop the game timer (only works when not typing in an input field)', 'Start/Stopp der Spielzeit (nur wenn kein Eingabefeld aktiv ist)')}</li>
                <li><strong>{L('H or B Key:', 'Taste H oder B:')}</strong> {L('Sound the horn (only works when not typing in an input field)', 'Horn auslösen (nur wenn kein Eingabefeld aktiv ist)')}</li>
              </ul>

            </div>
          </section>



            </>
          ) : (
            <>
            <WebManual variant="entertainment" lang={pageLanguage === 'de' ? 'de' : 'en'} />

            <div className="my-14 border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-semibold text-gray-900">{L('Reference: all details', 'Nachschlagen: alle Details')}</h2>
              <p className="mt-2 text-gray-600">
                {L(
                  'The step-by-step guide above covers everyday use. Below is the complete reference, including the rules the board applies by itself.',
                  'Die Anleitung oben deckt den Alltag ab. Darunter folgt die vollständige Referenz — inklusive der Regeln, die die Anzeigetafel selbst anwendet.'
                )}
              </p>
            </div>
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{L('Entertainment System', 'Entertainment-System')}</h2>
            <div className="space-y-4">
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <p className="text-blue-700">
                  <strong>{L('License Required:', 'Lizenz erforderlich:')}</strong>{' '}
                  {L(
                    'The entertainment system features require a valid license key to activate. Contact your system administrator or the software provider to obtain a license.',
                    'Die Entertainment-Funktionen benötigen einen gültigen Lizenzschlüssel. Wende dich an den Systemadministrator oder den Anbieter, um eine Lizenz zu erhalten.'
                  )}
                </p>
              </div>

              <h3 className="text-xl font-medium">{L('YouTube Music Player', 'YouTube Musik Player')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('Stream music from YouTube during breaks and pre-game', 'YouTube-Musik während Pause und vor dem Spiel abspielen')}</li>
                <li>{L('Support for both single videos and playlists', 'Unterstützt einzelne Videos und Playlists')}</li>
                <li>{L('Playback controls (play/pause)', 'Wiedergabe-Steuerung (Play/Pause)')}</li>
                <li>{L('Music automatically pauses when game starts', 'Musik pausiert automatisch, wenn das Spiel startet')}</li>
                <li>{L('Configure playlist URL in settings', 'Playlist-URL in den Einstellungen konfigurieren')}</li>
              </ul>

              <h3 className="text-xl font-medium mt-4">{L('In-Game Music System', 'In-Game Musik')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('Upload and manage up to 10 music tracks', 'Bis zu 10 Musik-Tracks hochladen und verwalten')}</li>
                <li>{L('Supported formats: MP3, WAV', 'Unterstützte Formate: MP3, WAV')}</li>
                <li>{L('Maximum file size: 10MB per track', 'Maximale Dateigröße: 10MB pro Track')}</li>
                <li>{L('Keyboard shortcuts:', 'Tastenkürzel:')}
                  <ul className="list-disc pl-6 mt-2">
                    <li><strong>{L('Ctrl + 1-0 Keys:', 'Ctrl + 1–0:')}</strong> {L('Play/stop corresponding track', 'Entsprechenden Track starten/stoppen')}</li>
                    <li><strong>{L('Press Ctrl twice:', 'Ctrl zweimal drücken:')}</strong> {L('Show the In-Game overlay', 'In-Game Overlay anzeigen')}</li>
                  </ul>
                </li>
                <li>{L('Perfect for quick sound effects or celebration music', 'Ideal für kurze Jingles oder Tor-Musik')}</li>
                <li>{L('The music automatically stops when the game resumes', 'Musik stoppt automatisch beim Wiederanpfiff')}</li>
                <li>{L('Tracks persist between sessions', 'Tracks bleiben zwischen Sitzungen erhalten')}</li>
              </ul>

              <h3 className="text-xl font-medium mt-4">{L('Multimedia Video System', 'Multimedia Videos')}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{L('Display videos before matches, during breaks, or for entertainment', 'Videos vor dem Spiel, während Pausen oder zur Unterhaltung anzeigen')}</li>
                <li>{L('Support for up to 4 different videos', 'Unterstützt bis zu 4 verschiedene Videos')}</li>
                <li>{L('Compatible with YouTube videos', 'Kompatibel mit YouTube-Videos')}</li>
                <li>{L('Features:', 'Funktionen:')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('Full-screen mode support', 'Vollbildmodus')}</li>
                    <li>{L('Loop option for continuous playback', 'Loop-Option (Wiederholen)')}</li>
                    <li>{L('Automatic playback options', 'Optionen für automatische Wiedergabe')}</li>
                  </ul>
                </li>
                <li>{L('Perfect for:', 'Ideal für:')}
                  <ul className="list-disc pl-6 mt-2">
                    <li>{L('Sponsor advertisements', 'Sponsoren-Werbung')}</li>
                    <li>{L('Team introductions', 'Teamvorstellungen')}</li>
                    <li>{L('League announcements', 'Liga-Ankündigungen')}</li>
                    <li>{L('Entertainment during breaks', 'Unterhaltung während Pausen')}</li>
                  </ul>
                </li>
              </ul>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">{L('Entertainment System Tips:', 'Tipps:')}</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{L('Configure all media before the match starts', 'Medien am besten vor Spielbeginn konfigurieren')}</li>
                  <li>{L('Test sound levels during setup', 'Sound/Lautstärke beim Setup testen')}</li>
                  <li>{L('Ensure stable internet connection for YouTube features', 'Stabile Internetverbindung für YouTube sicherstellen')}</li>
                  <li>{L('Use keyboard shortcuts for quick music control during the game', 'Tastenkürzel für schnelle Musiksteuerung nutzen')}</li>
                  <li>{L('Videos and music automatically stop when game timer starts', 'Videos/Musik stoppen automatisch beim Start der Spielzeit')}</li>
                </ul>
              </div>
            </div>
          </section>


            </>
          )}
        </div>
      </main>

      <FooterSection />
    </div>
  );
};

export default Manual;
